import React, { useEffect, useMemo, useRef, useState } from 'react';
import { handleKeyEvent } from '../../../../util/events.util';
import { PromiseOr } from '../../../../util/types.util';
import { LoadingSpinner } from '../../LoadingSpinner';

interface DropdownItemProps {
  children: React.ReactNode;
  onClick?: () => PromiseOr<void>;
  selected?: boolean;
}

export default function DropdownItem({ children, onClick, selected = false }: DropdownItemProps) {
  const ref = useRef<HTMLLIElement>(null);

  const [loading, setLoading] = useState(false);

  const style = useMemo(() => {
    if (selected) {
      return 'bg-gray-100 active:bg-primary-50 !border-primary-600 border-solid box-border';
    }
  }, [onClick]);

  const cursorClass = useMemo(() => {
    if (onClick) return 'cursor-pointer';

    return '';
  }, []);

  const clickHandler = async () => {
    if (!onClick) return undefined;
    if (loading) return;
    setLoading(true);
    await onClick();
    setLoading(false);
  };

  useEffect(() => {
    if (!ref.current) return;
    const ac = new AbortController();
    if (selected) {
      ref.current.focus();
      document.addEventListener('keydown', e => handleKeyEvent(e, 'Enter', () => clickHandler()), ac);
    }

    return () => ac.abort();
  }, [selected, ref, ref.current]);

  return (
    <li
      ref={ref}
      role='option'
      onClick={event => {
        event.stopPropagation();
        clickHandler();
      }}
      className={`${style} ${cursorClass} flex items-center gap-2 border-l-2 border-transparent stroke-gray-900 px-3 py-2 text-left text-sm`}
    >
      <div className='flex-grow overflow-hidden'>{children}</div>
      {loading && <LoadingSpinner />}
    </li>
  );
}
